import { useState, useEffect } from "react";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DocumentUpload from "../../components/DocumentUpload";
import InputField from "../../components/InputField";
import DropDown from "../../components/DropDown";
import { getStateFromGST } from "../../constants/gstStateCodes";
import { toast } from "react-toastify";
import {
  validatePAN,
  validateGST,
  validateMobile,
  validateIFSC,
  validateAccountNumber,
  validateAadhar,
} from "../../utils/validationUtils";
import { validateGSTPANMatch } from "../../utils/gstPanValidation";
import { validatePANEntityType } from "../../utils/panEntityValidation";
import { uploadOnboardingDocumentAction } from "../../actions/uploadOnboardingDocumentAction";
import { convertToBoolean } from "../../utils/dataConversionUtils";
import { getUserData } from "../../utils/authnicate";
import { fetchKYCDetailsByIdAction } from "../../actions/fetchKYCDetailsByIdAction";
import { useAadharVisibilityData } from "./AadharVisibilityData";
import {
  ENTITY_INFO_FIELDS,
  GST_FIELDS,
  MSME_FIELDS,
  CONTACT_FIELDS,
  BANK_FIELDS,
  ALL_FIELDS,
  UTILITY_FIELDS,
  THIRD_PARTY_FIELDS,
  ENTITY_TYPE_DOCUMENTS,
  DOCS_BANK,
  DOC_KEY_TO_API_TYPE,
  getCityOptions,
} from "./UploadDetailsFields";
import { sendEntityKYCFormDataAction } from "../../actions/entityKYCFormDataAction";
import { parseDeficiencies } from "../../utils/deficiencyUtils";
// usePincodeAutofill disabled — state/city selected via dropdowns
// import usePincodeAutofill from "../../hooks/usePincodeAutofill";

export {
  ENTITY_INFO_FIELDS,
  GST_FIELDS,
  MSME_FIELDS,
  CONTACT_FIELDS,
  BANK_FIELDS,
  UTILITY_FIELDS,
  THIRD_PARTY_FIELDS,
  ENTITY_TYPE_DOCUMENTS,
  DOCS_BANK,
};

// ── Entity-type required documents section ────────────────────────────────────

export const EntityDocumentsSection = ({
  entityType,
  entityFiles,
  onEntityFileChange,
  entityDocInputs = {},
  onEntityDocInputChange,
  onEntityDocInputBlur,
  existingDocuments = [],
  errors = {},
  deficiencyMap = {},
  isDeficiencyMode = false,
}) => {
  const docs = ENTITY_TYPE_DOCUMENTS[entityType];
  if (!docs || docs.length === 0) return null;

  const getExisting = (key) => {
    const doc = existingDocuments.find((d) => d.key === key);
    return doc?.files?.find((f) => f.isUploaded) || null;
  };

  return (
    <div className="entity-docs-section">
      <p className="entity-docs-note">
        Required documents for <strong>{entityType.replace(/_/g, " ")}</strong>
      </p>
      <div className="entity-docs-list">
        {docs.map((doc) => {
          const isDeficient = !!deficiencyMap[doc.key];
          const uploadable = !isDeficiencyMode || isDeficient;
          const fieldName = doc.inputField?.name;

          // Debug: log deficiency state for each doc
          if (isDeficiencyMode) {
            // console.log(
            //   `[EntityDocs] key=${doc.key} isDeficient=${isDeficient} uploadable=${uploadable} fieldName=${fieldName}`,
            // );
          }

          // Aadhar number inputs — ALWAYS editable and ALWAYS blank on load
          // Never auto-filled, never readonly — user must type fresh every time
          const isAadharInputField =
            fieldName &&
            [
              "doc_aadhar_number",
              "doc_aadharBoth_numbers",
              "doc_aadharBothSecondary_numbers",
              "doc_directorAadhar_number",
              "doc_aadharTrustee_number",
            ].includes(fieldName);

          // Aadhar fields: always editable regardless of deficiency/readonly mode
          const inputEditable = isAadharInputField || uploadable;

          return (
            <div
              key={doc.key}
              className={`entity-doc-row ${isDeficient ? "entity-doc-row--deficient" : ""}`}
            >
              {doc.inputField && (
                <InputField
                  name={fieldName}
                  label={`${doc.label} Number`}
                  type="text"
                  value={entityDocInputs[fieldName] || ""}
                  onChange={
                    inputEditable && onEntityDocInputChange
                      ? (e) => onEntityDocInputChange(fieldName, e.target.value)
                      : undefined
                  }
                  onBlur={
                    inputEditable && onEntityDocInputBlur
                      ? (e) => onEntityDocInputBlur(fieldName, e.target.value)
                      : undefined
                  }
                  placeholder={doc.inputField.placeholder}
                  maxLength={doc.inputField.maxLength}
                  required={doc.required}
                  error={errors[fieldName] || ""}
                  readOnly={!inputEditable}
                  className={isDeficient ? "input--deficient" : ""}
                />
              )}
              <div className={isDeficient ? "doc-deficient-wrap" : ""}>
                <DocumentUpload
                  label={`Upload ${doc.label}`}
                  name={doc.key}
                  file={entityFiles[doc.key] || null}
                  existingFile={getExisting(doc.key)}
                  onChange={(e) => {
                    if (!uploadable) return;
                    onEntityFileChange(doc.key, e.target.files[0]);
                  }}
                  required={doc.required}
                  disabled={!uploadable}
                />
                {isDeficient && (
                  <span className="doc-deficient-label">
                    ⚠ Deficiency — please re-upload
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Field renderer ─────────────────────────────────────────────────────────────

export const renderFields = (
  fields,
  formData,
  errors,
  handleChange,
  handleBlur,
) => {
  const rendered = [];
  let i = 0;
  while (i < fields.length) {
    const field = fields[i];
    const show =
      !field.conditionalOn ||
      formData[field.conditionalOn] === field.conditionalValue;

    // Collect all consecutive fields with the same group tag
    if (field.group) {
      const groupName = field.group;
      const groupFields = [];
      while (i < fields.length && fields[i].group === groupName) {
        groupFields.push(fields[i]);
        i++;
      }
      rendered.push(
        <div
          key={groupName}
          className={`field-group field-group--${groupName}`}
        >
          {groupFields.map((gf) => {
            const gShow =
              !gf.conditionalOn ||
              formData[gf.conditionalOn] === gf.conditionalValue;
            if (!gShow) return null;
            const gValue = formData[gf.name] || "";
            const gError = errors[gf.name] || "";

            // Select fields inside a group (State, City)
            if (gf.type === "select") {
              // City options depend on the currently selected State
              const resolvedOptions =
                gf.name === "city"
                  ? getCityOptions(formData.State || formData.state || "")
                  : gf.options || [];
              return (
                <DropDown
                  key={gf.name}
                  name={gf.name}
                  label={gf.label}
                  value={gValue}
                  onChange={(e) => {
                    if (handleChange) handleChange(e);
                    // When state changes, reset city
                    if (gf.name === "State" && handleChange) {
                      handleChange({ target: { name: "city", value: "" } });
                    }
                  }}
                  options={resolvedOptions}
                  error={gError}
                  required={gf.required}
                  fullWidth
                />
              );
            }

            return (
              <InputField
                key={gf.name}
                name={gf.name}
                label={gf.label}
                type={gf.type}
                value={gValue}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={gf.placeholder}
                error={gError}
                required={gf.required}
                maxLength={gf.maxLength}
                readOnly={gf.readOnly}
              />
            );
          })}
        </div>,
      );
      continue;
    }

    if (!show) {
      i++;
      continue;
    }
    const value = formData[field.name] || "";
    const error = errors[field.name] || "";
    if (field.type === "select") {
      // Resolve options — use entity-type-specific options if available
      const resolvedOptions =
        field.optionsByEntityType?.[formData.entityType] ?? field.options;
      rendered.push(
        <DropDown
          key={field.name}
          name={field.name}
          value={value}
          onChange={handleChange}
          label={field.label}
          options={resolvedOptions}
          error={error}
          required={field.required}
          fullWidth
        />,
      );
    } else {
      rendered.push(
        <InputField
          key={field.name}
          name={field.name}
          label={field.label}
          type={field.type}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={field.placeholder}
          error={error}
          required={field.required}
          maxLength={field.maxLength}
          rows={field.rows}
          readOnly={field.readOnly}
        />,
      );
    }
    i++;
  }
  return rendered;
};

// ── Initial state ──────────────────────────────────────────────────────────────

export const initialDetailsState = {
  parentName: "",
  tradeName: "",
  address: "",
  pinCode: "",
  city: "",
  State: "",
  natureOfBusiness: "",
  isInvoiceRequired: "",
  entityType: "",
  CompanypanNumber: "",
  isGSTRegistered: "",
  utilityType: "",
  utilityProviderName: "",
  utilityAccountNumber: "",
  utilityAddress: "",
  isMSMERegistered: "",
  msmeNumber: "",
  contactPersonName: "",
  contactPersonDesignation: "",
  contactPersonMobile: "",
  contactEmail: "",
  hasBankDetails: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  accountType: "",
  branchName: "",
  beneficiaryName: "",
  thirdPartyRelation: "",
  thirdPartyAccountNumber: "",
  thirdPartyIfscCode: "",
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export const useUploadDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialDetailsState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // GST entries state
  const [gstEntries, setGstEntries] = useState([
    { gstNumber: "", gstState: "", gstAddress: "" },
  ]);
  const [gstFiles, setGstFiles] = useState([null]);
  const [gstErrors, setGstErrors] = useState([""]);

  // Document file states
  const [utilityFile, setUtilityFile] = useState(null);
  const [msmeFile, setMsmeFile] = useState(null);
  const [thirdPartyFile, setThirdPartyFile] = useState(null);
  const [entityFiles, setEntityFiles] = useState({});
  const [entityDocInputs, setEntityDocInputs] = useState({});
  const [bankFiles, setBankFiles] = useState({});
  const [existingDocuments, setExistingDocuments] = useState([]);
  // Existing paths from API prefill — used as fallback when user doesn't re-upload
  const [existingPaths, setExistingPaths] = useState({});
  // isXxx flags from API — passed through as-is in the payload (not derived from hasPath)
  const [existingIsFlags, setExistingIsFlags] = useState({});
  // kycId from API prefill — passed through in payload
  const [existingKycId, setExistingKycId] = useState(0);
  // Deficiency mode — set when any *Def flag is true in the API record
  const [deficiencyMap, setDeficiencyMap] = useState({});
  const [isDeficiencyMode, setIsDeficiencyMode] = useState(false);

  // Stores upload API responses keyed by documentType
  // e.g. { PassbookOrCancelledChequePath: { id, fileName, filePath, documentType, extension } }
  const [uploadedDocs, setUploadedDocs] = useState({});

  const handleEntityFileChange = (key, file) =>
    setEntityFiles((prev) => ({ ...prev, [key]: file }));

  const handleEntityDocInputChange = (name, value) => {
    // Aadhar fields: digits only, max 12
    const aadharFields = [
      "doc_aadhar_number",
      "doc_aadharBoth_numbers",
      "doc_aadharBothSecondary_numbers",
      "doc_aadharTrustee_number",
      "doc_directorAadhar_number",
    ];
    if (aadharFields.includes(name)) {
      value = value.replace(/\D/g, "").slice(0, 12);
    }
    setEntityDocInputs((prev) => ({ ...prev, [name]: value }));

    // PAN entity type check — runs while typing once PAN is fully valid
    const panEntityFields = ["doc_pan_number", "doc_panTrust_number"];
    if (panEntityFields.includes(name) && formData.entityType) {
      const entityCheck = validatePANEntityType(value, formData.entityType);
      // console.log("[PAN Entity Check]", {
      //   pan: value,
      //   entityType: formData.entityType,
      //   result: entityCheck,
      // });
      if (!entityCheck.isValid) {
        setErrors((prev) => ({ ...prev, [name]: entityCheck.message }));
        toast.error(`⚠️ PAN Mismatch: ${entityCheck.message}`, {
          position: "top-right",
          autoClose: 6000,
          toastId: "pan-entity-mismatch",
        });
      } else {
        setErrors((prev) => {
          const e = { ...prev };
          if (
            e[name] &&
            e[name].includes("does not match selected entity type")
          ) {
            delete e[name];
          }
          return e;
        });
      }
    }
  };

  // Blur validation for document number inputs
  const handleEntityDocInputBlur = (name, value) => {
    if (!value || !value.trim()) return;
    const validators = {
      doc_aadhar_number: validateAadhar,
      doc_aadharBoth_numbers: validateAadhar,
      doc_aadharBothSecondary_numbers: validateAadhar,
      doc_aadharTrustee_number: validateAadhar,
      doc_directorAadhar_number: validateAadhar,
      doc_pan_number: validatePAN,
      doc_directorPan_number: validatePAN,
      doc_panTrust_number: validatePAN,
      doc_panTrustee_number: validatePAN,
    };
    const validator = validators[name];
    if (!validator) return;
    const result = validator(value);

    setErrors((prev) => {
      const e = { ...prev };
      if (result !== true) {
        e[name] = result;
        return e;
      }
      delete e[name];

      // Cross-check: PAN and Director PAN must not be the same
      const panValue =
        name === "doc_pan_number"
          ? value.trim().toUpperCase()
          : (entityDocInputs["doc_pan_number"] || "").trim().toUpperCase();
      const directorPanValue =
        name === "doc_directorPan_number"
          ? value.trim().toUpperCase()
          : (entityDocInputs["doc_directorPan_number"] || "")
              .trim()
              .toUpperCase();

      if (
        (name === "doc_pan_number" || name === "doc_directorPan_number") &&
        panValue &&
        directorPanValue &&
        panValue === directorPanValue
      ) {
        e["doc_pan_number"] = "PAN Card and Director PAN must not be the same";
        e["doc_directorPan_number"] =
          "Director PAN must be different from PAN Card";
      } else {
        // Clear the cross-check errors if they no longer apply
        if (
          e["doc_pan_number"] ===
          "PAN Card and Director PAN must not be the same"
        ) {
          delete e["doc_pan_number"];
        }
        if (
          e["doc_directorPan_number"] ===
          "Director PAN must be different from PAN Card"
        ) {
          delete e["doc_directorPan_number"];
        }
      }

      // PAN entity type check — for main entity PAN and Trust PAN
      const panEntityFields = ["doc_pan_number", "doc_panTrust_number"];
      if (panEntityFields.includes(name) && formData.entityType) {
        const entityCheck = validatePANEntityType(value, formData.entityType);
        if (!entityCheck.isValid) {
          e[name] = entityCheck.message;
          toast.error(`⚠️ PAN Mismatch: ${entityCheck.message}`, {
            position: "top-right",
            autoClose: 6000,
            toastId: "pan-entity-mismatch",
          });
        }
      }

      return e;
    });
  };

  const handleBankFileChange = (key, file) =>
    setBankFiles((prev) => ({ ...prev, [key]: file }));

  // const departmentOptions = useDepartmentOptions(); // TODO: use when department dropdown is added
  const [beneficiaryMatchStatus, setBeneficiaryMatchStatus] = useState(null);
  // null = not checked yet | "match" | "mismatch"

  const showThirdParty =
    formData.hasBankDetails === "yes" &&
    formData.tradeName.trim() !== "" &&
    formData.beneficiaryName.trim() !== "" &&
    formData.tradeName.trim().toLowerCase() !==
      formData.beneficiaryName.trim().toLowerCase();

  // URL params from step 1
  const entityCategory = searchParams.get("entityCategory") || "";
  // const entityLOBType = searchParams.get("entityLOBType") || "";         // TODO: show in summary
  // const lobNationality = searchParams.get("lobNationality") || "Domestic"; // TODO: include in payload when needed
  // const aaDepartmentFunction = searchParams.get("aaDepartmentFunction") || ""; // TODO: include in payload when needed
  const contactPersonEmail = searchParams.get("contactPersonEmail") || "";
  const spocEmailAddress = searchParams.get("spocEmailAddress") || "";
  const entityNameFromUrl = searchParams.get("EntityName") || "";
  const onboardingIdFromUrl =
    searchParams.get("onboardingId") ||
    searchParams.get("entityPrimaryId") ||
    "";

  const entityId =
    searchParams.get("entityId") || searchParams.get("EntityId") || "";

  const modifiedbyFromURL = searchParams.get("type");
  // console.log("MODIFIED : ", modifiedbyFromURL);

  // ── Fetch existing KYC data by onboardingId from URL ──────────────────────
  useEffect(() => {
    if (!onboardingIdFromUrl) return;
    const id = parseInt(onboardingIdFromUrl, 10);
    if (!id) return;

    fetchKYCDetailsByIdAction(id).then(
      ({ success, data: record, documents }) => {
        if (!success || !record) return;

        // ── Nested sub-objects ──────────────────────────────────────────────
        const prop = record.proprietorshipDetails || {};
        const partner = record.partnershipDetails || {};
        const llp = record.llpDetails || {};
        const pvt = record.privatePublicLimitedCompanyDetails || {};
        const trust = record.trustDetails || {};
        const msme = record.msmeDetails || {};
        const utility = record.utilityBillDetails || {};
        const bank = record.bankDetails || {};
        const contact = record.contactPersonDetails || {};
        const pan = record.panDetails || {};
        const aadhar = record.adharDetails || {};

        setExistingDocuments(documents || []);
        setExistingKycId(record.kycId || 0);

        // Parse deficiency flags from the raw record
        const { deficiencyMap: dMap, isDeficiencyMode: dMode } =
          parseDeficiencies(record);
        console.log("[Deficiency] dMap:", dMap, "| isDeficiencyMode:", dMode);
        setDeficiencyMap(dMap);
        setIsDeficiencyMode(dMode);

        // Seed existing paths for fallback when user doesn't re-upload
        setExistingPaths({
          AadharCardPath: prop.aadharCardPath || aadhar.aadharCardPath || "",
          PANCardPath: prop.panCardPath || pan.panCardPath || "",
          PartnershipDeedPath:
            partner.partnershipDeedPath || llp.partnershipDeedPath || "",
          UploadLLPCertificatePath: llp.uploadLLPCertificatePath || "",
          AadharOfBothPartnersPath:
            partner.aadharOfBothPartnersPath ||
            llp.aadharOfBothPartnersPath ||
            "",
          AadharOfBothPartnersPathSecond:
            partner.aadharOfBothPartnersPathSecond ||
            llp.aadharOfBothPartnersPathSecond ||
            "",
          CertificateOfIncorporationPath:
            pvt.certificateOfIncorporationPath || "",
          DirectorPANPath: pvt.directorPANPath || "",
          DirectorAadharPath: pvt.directorAadharPath || "",
          MSMECertificatePath: msme.msmeCertificatePath || "",
          UtilityBillDocumentPath: utility.utilityBillDocumentPath || "",
          PassbookOrCancelledChequePath:
            bank.passbookOrCancelledChequePath || "",
          UploadSignedFormPath: bank.uploadSignedFormPath || "",
          TrustDeedPath: trust.trustDeedPath || "",
          PANOfTrustPath: trust.trustPanPath || "",
          AadharOfTrusteePath: trust.trusteeAadharPath || "",
          PANOfTrusteePath: trust.trusteePanPath || "",
        });

        // Seed isXxx flags exactly as received from the API — passed through unchanged in payload
        setExistingIsFlags({
          isPANCard: prop.isPANCard ?? pan.isPANCard ?? null,
          isAadharCard: prop.isAadharCard ?? aadhar.isAadharCard ?? null,
          isPartnershipDeed:
            partner.isPartnershipDeed ?? llp.isPartnershipDeed ?? null,
          isAadharOfBothPartners:
            partner.isAadharOfBothPartners ??
            llp.isAadharOfBothPartners ??
            null,
          isLLPCertificate: llp.isLLPCertificate ?? null,
          isCertificateOfIncorporation:
            pvt.isCertificateOfIncorporation ?? null,
          isDirectorPan: pvt.isDirectorPan ?? null,
          isDirectorAadhar: pvt.isDirectorAadhar ?? null,
          isTrustDeed: trust.isTrustDeed ?? null,
          isPANOfTrust: trust.isPANOfTrust ?? null,
          isPANOfTrustee: trust.isPANOfTrustee ?? null,
          isAadharOfTrustee: trust.isAadharOfTrustee ?? null,
          isMSMECertificate: msme.isMSMECertificate ?? null,
          isUtilityBillDocument: utility.isUtilityBillDocument ?? null,
          isPassbookOrCancelledCheque: bank.isPassbookOrCancelledCheque ?? null,
          isSignedForm: bank.isSignedForm ?? null,
          // GST isGST flags — keyed by index
          ...(Array.isArray(record.gstList)
            ? Object.fromEntries(
                record.gstList.map((g, i) => [`isGST_${i}`, g.isGST ?? null]),
              )
            : {}),
        });

        const boolToYesNo = (v) =>
          v === true ? "yes" : v === false ? "no" : "";

        setFormData((prev) => ({
          ...prev,
          // Entity info
          parentName: record.entityName || entityNameFromUrl || prev.parentName,
          tradeName: record.tradeName || "",
          pinCode: record.pinCode || "",
          city: record.city || "",
          State: record.state || "",
          address: record.address || "",
          natureOfBusiness: record.natureOfBusiness || "",
          isInvoiceRequired: boolToYesNo(record.invoiceRequired),
          entityType: record.entityType || "",
          CompanypanNumber: prop.panCardNumber || pan.panCardNumber || "",
          // GST
          isGSTRegistered: boolToYesNo(record.isGSTRegistered),
          // Utility
          utilityType: utility.utilityType || "",
          utilityProviderName: utility.providerName || "",
          utilityAccountNumber: utility.accountOrConsumerNumber || "",
          utilityAddress: utility.addressOnUtilityBill || "",
          // MSME
          isMSMERegistered: boolToYesNo(msme.isRegisteredUnderMSME),
          msmeNumber: msme.msmeRegistrationNumber || "",
          // Contact
          contactPersonName: contact.contactPersonName || "",
          contactPersonDesignation: contact.designation || "",
          contactPersonMobile: contact.mobileNumber || "",
          contactEmail: contact.contactPersonEmail || record.emailAddress || "",
          // Bank
          hasBankDetails: boolToYesNo(bank.provideBankDetails),
          bankName: bank.bankName || "",
          accountNumber: bank.accountNumber || "",
          ifscCode: bank.ifscCode || "",
          accountType: bank.accountType || "",
          branchName: bank.branchName || "",
          beneficiaryName: bank.beneficiaryName || "",
          relationWithBeneficiary: bank.relationWithBeneficiary || "",
          thirdPartyRelation: bank.relationWithBeneficiary || "",
        }));

        // Document number inputs
        setEntityDocInputs({
          // Aadhar number fields — NEVER auto-filled, always blank for security
          doc_aadhar_number: "",
          doc_aadharBoth_numbers: "",
          doc_aadharBothSecondary_numbers: "",
          doc_directorAadhar_number: "",
          doc_aadharTrustee_number: "",
          // All other fields auto-fill from API
          doc_pan_number: prop.panCardNumber || pan.panCardNumber || "",
          doc_llpCertificate_number: llp.llpCrtificateNumber || "",
          doc_partnershipDeed_ref: partner.partnershipDeedPath ? "" : "",
          doc_coi_number: pvt.certificateOfIncorporationNumber || "",
          doc_directorPan_number: pvt.directorPANNumber || "",
          doc_trustDeed_ref: trust.trustDeedNumber || "",
          doc_panTrust_number: trust.trustPanNumber || "",
          doc_panTrustee_number: trust.trusteePanNumber || "",
        });

        // GST entries
        if (Array.isArray(record.gstList) && record.gstList.length > 0) {
          setGstEntries(
            record.gstList.map((g) => ({
              gstId: g.gstId ?? 0, // preserve real gstId from API
              gstNumber: g.gstNumber || "",
              gstState: g.state || "",
              gstAddress: g.gstAddress || "",
            })),
          );
          setGstFiles(record.gstList.map(() => null));
          setGstErrors(record.gstList.map(() => ""));
          setExistingPaths((prev) => {
            const updated = { ...prev };
            record.gstList.forEach((g, i) => {
              if (g.gstCertificatePath)
                updated[`gst_file_${i}`] = g.gstCertificatePath;
            });
            return updated;
          });
        }
      },
    );
  }, [onboardingIdFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // GST entry handlers
  const addGstEntry = () => {
    setGstEntries((prev) => [
      ...prev,
      { gstId: 0, gstNumber: "", gstState: "", gstAddress: "" },
    ]);
    setGstFiles((prev) => [...prev, null]);
    setGstErrors((prev) => [...prev, ""]);
  };
  const removeGstEntry = (index) => {
    setGstEntries((prev) => prev.filter((_, i) => i !== index));
    setGstFiles((prev) => prev.filter((_, i) => i !== index));
    setGstErrors((prev) => prev.filter((_, i) => i !== index));
  };
  const handleGstChange = (index, field, value) => {
    setGstEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry;
        const updated = {
          ...entry,
          [field]: field === "gstNumber" ? value.toUpperCase() : value,
        };
        if (field === "gstNumber") {
          updated.gstState = getStateFromGST(value.toUpperCase());
          setGstErrors((errs) => errs.map((e, ei) => (ei === index ? "" : e)));
        }
        return updated;
      }),
    );
  };
  const handleGstBlur = (index) => {
    const gstNumber = gstEntries[index]?.gstNumber || "";
    if (!gstNumber) return;

    // First validate GST format
    const gstFormatResult = validateGST(gstNumber);
    if (gstFormatResult !== true) {
      setGstErrors((prev) =>
        prev.map((e, ei) => (ei === index ? gstFormatResult : e)),
      );
      return;
    }

    // Then validate GST-PAN match
    const companyPAN =
      formData.CompanypanNumber || entityDocInputs["doc_pan_number"] || "";
    if (companyPAN) {
      const panMatchResult = validateGSTPANMatch(gstNumber, companyPAN);
      if (!panMatchResult.isValid) {
        setGstErrors((prev) =>
          prev.map((e, ei) => (ei === index ? panMatchResult.message : e)),
        );
        toast.error(`GST Entry ${index + 1}: ${panMatchResult.message}`, {
          position: "top-right",
          autoClose: 8000,
        });
        return;
      }
    }

    // Clear error if all validations pass
    setGstErrors((prev) => prev.map((e, ei) => (ei === index ? "" : e)));
  };
  const handleGstFileChange = (index, file) => {
    setGstFiles((prev) => prev.map((f, i) => (i === index ? file : f)));
  };

  // Seed entity name from URL param — used when no API prefill, or as initial value
  useEffect(() => {
    if (entityNameFromUrl) {
      setFormData((prev) => ({
        ...prev,
        // Only set if not already filled by API prefill
        parentName: prev.parentName || entityNameFromUrl,
      }));
    }
  }, [entityNameFromUrl]);

  // Pincode auto-fill
  // Pincode API auto-fill is disabled — state/city are now selected via dropdowns.
  // usePincodeAutofill({
  //   pincode: formData.pinCode,
  //   skipOnMount: !!(onboardingIdFromUrl && formData.city),
  //   onSuccess: ({ city, state }) => {
  //     setFormData((prev) => ({ ...prev, city, State: state }));
  //     setErrors((prev) => {
  //       const e = { ...prev };
  //       delete e.pinCode;
  //       return e;
  //     });
  //   },
  //   onError: (msg) => {
  //     setFormData((prev) => ({ ...prev, city: "", State: "" }));
  //     setErrors((prev) => ({ ...prev, pinCode: msg }));
  //   },
  // });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    const uppercaseFields = [
      "CompanypanNumber",
      "ifscCode",
      "thirdPartyIfscCode",
    ];
    if (uppercaseFields.includes(name) && typeof newValue === "string")
      newValue = newValue.toUpperCase();

    const digitsOnlyFields = [
      "contactPersonMobile",
      "accountNumber",
      "thirdPartyAccountNumber",
    ];
    if (digitsOnlyFields.includes(name) && typeof newValue === "string")
      newValue = newValue.replace(/\D/g, "");

    if (name === "pinCode") {
      newValue = newValue.replace(/\D/g, "").slice(0, 6);
    }

    if (name === "hasBankDetails" && newValue === "no") {
      setErrors((prev) => {
        const e = { ...prev };
        [
          "bankName",
          "accountNumber",
          "ifscCode",
          "accountType",
          "branchName",
          "beneficiaryName",
        ].forEach((f) => delete e[f]);
        return e;
      });
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    if (name === "tradeName" || name === "beneficiaryName") {
      setBeneficiaryMatchStatus(null);
    }

    if (type !== "checkbox" && errors[name]) {
      setErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
    }

    // PAN entity type check — runs while typing once PAN is fully valid
    if (name === "CompanypanNumber" && type !== "checkbox") {
      const entityType = formData.entityType;
      if (entityType) {
        const entityCheck = validatePANEntityType(newValue, entityType);
        // console.log("[PAN Entity Check]", {
        //   pan: newValue,
        //   entityType,
        //   result: entityCheck,
        // });
        if (!entityCheck.isValid) {
          setErrors((prev) => ({ ...prev, [name]: entityCheck.message }));
          toast.error(`⚠️ PAN Mismatch: ${entityCheck.message}`, {
            position: "top-right",
            autoClose: 6000,
            toastId: "pan-entity-mismatch",
          });
        } else {
          // Clear the entity-type error once PAN matches again
          setErrors((prev) => {
            const e = { ...prev };
            if (
              e[name] &&
              e[name].includes("does not match selected entity type")
            ) {
              delete e[name];
            }
            return e;
          });
        }
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (!value || !value.trim()) return;

    if (name === "beneficiaryName" || name === "tradeName") {
      const trade = (name === "tradeName" ? value : formData.tradeName)
        .trim()
        .toLowerCase();
      const beneficiary = (
        name === "beneficiaryName" ? value : formData.beneficiaryName
      )
        .trim()
        .toLowerCase();
      if (trade && beneficiary) {
        setBeneficiaryMatchStatus(trade === beneficiary ? "match" : "mismatch");
      }
      return;
    }

    const validators = {
      // Form fields
      CompanypanNumber: validatePAN,
      contactPersonMobile: validateMobile,
      ifscCode: validateIFSC,
      accountNumber: validateAccountNumber,
      thirdPartyIfscCode: validateIFSC,
      thirdPartyAccountNumber: validateAccountNumber,
      // Document number input fields (entityDocInputs)
      doc_aadhar_number: validateAadhar,
      doc_aadharBoth_numbers: validateAadhar,
      doc_aadharBothSecondary_numbers: validateAadhar,
      doc_aadharTrustee_number: validateAadhar,
      doc_directorAadhar_number: validateAadhar,
      doc_pan_number: validatePAN,
      doc_directorPan_number: validatePAN,
      doc_panTrust_number: validatePAN,
      doc_panTrustee_number: validatePAN,
    };
    const validator = validators[name];
    if (validator) {
      const result = validator(value);
      if (result !== true) {
        setErrors((prev) => ({ ...prev, [name]: result }));
        return;
      }
      setErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
    }

    // PAN entity type check — for CompanypanNumber field
    if (name === "CompanypanNumber" && formData.entityType) {
      const entityCheck = validatePANEntityType(value, formData.entityType);
      if (!entityCheck.isValid) {
        setErrors((prev) => ({ ...prev, [name]: entityCheck.message }));
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    ALL_FIELDS.forEach((field) => {
      // Skip bank fields entirely when user chose not to provide bank details
      if (
        BANK_FIELDS.some((b) => b.name === field.name) &&
        formData.hasBankDetails !== "yes"
      )
        return;

      if (
        field.conditionalOn &&
        formData[field.conditionalOn] !== field.conditionalValue
      )
        return;
      const value = formData[field.name];
      if (field.required && (!value || value.toString().trim() === "")) {
        newErrors[field.name] = `${field.label} is required`;
      } else if (field.validation && value) {
        const result = field.validation(value);
        if (result !== true) newErrors[field.name] = result;
      }
    });

    // ── pinCode: must be exactly 6 numeric digits ─────────────────────────
    const pin = (formData.pinCode || "").replace(/\D/g, "");
    if (!pin) {
      newErrors.pinCode = "Pin Code is required";
    } else if (pin.length !== 6) {
      newErrors.pinCode = "Pin Code must be exactly 6 digits";
    }

    // ── State and City: mandatory dropdowns ───────────────────────────────
    if (!formData.State && !formData.state) {
      newErrors.State = "State is required";
    }
    if (!formData.city) {
      newErrors.city = "City is required";
    }

    if (formData.isGSTRegistered === "no") {
      UTILITY_FIELDS.forEach((field) => {
        if (field.required && !formData[field.name]?.trim())
          newErrors[field.name] = `${field.label} is required`;
      });

      // Utility bill document is required when GST is not registered
      const hasUtilityDocUploaded =
        utilityFile ||
        uploadedDocs["UtilityBillDocumentPath"] ||
        existingDocuments.some(
          (d) =>
            d.key === "doc_utility_bill" && d.files?.some((f) => f.isUploaded),
        ) ||
        existingPaths["UtilityBillDocumentPath"];

      if (!hasUtilityDocUploaded) {
        newErrors["doc_utility_bill"] =
          "Utility Bill Document (Address Proof) is required";
      }
    }

    if (showThirdParty) {
      THIRD_PARTY_FIELDS.forEach((field) => {
        const value = formData[field.name];
        if (field.required && (!value || value.toString().trim() === "")) {
          newErrors[field.name] = `${field.label} is required`;
        } else if (field.validation && value) {
          const result = field.validation(value);
          if (result !== true) newErrors[field.name] = result;
        }
      });
    }

    // Bank document upload — required when bank details are provided
    if (formData.hasBankDetails === "yes") {
      const hasBankDocUploaded =
        bankFiles["doc_bank_proof"] ||
        existingDocuments.some(
          (d) =>
            d.key === "doc_bank_proof" && d.files?.some((f) => f.isUploaded),
        );
      if (!hasBankDocUploaded) {
        newErrors["doc_bank_proof"] =
          "Bank Document (Passbook / Cancelled Cheque) is required";
      }
    }

    // Entity-type document validation — required docs must be uploaded
    if (formData.entityType) {
      const entityDocs = ENTITY_TYPE_DOCUMENTS[formData.entityType] || [];
      entityDocs.forEach((doc) => {
        if (!doc.required) return;

        // Check if file is uploaded (new upload or existing)
        const hasUploaded =
          entityFiles[doc.key] ||
          existingDocuments.some(
            (d) => d.key === doc.key && d.files?.some((f) => f.isUploaded),
          );

        if (!hasUploaded) {
          newErrors[`entity_doc_${doc.key}`] = `${doc.label} is required`;
        }

        // Also validate the number input if present
        if (doc.inputField) {
          const inputVal = entityDocInputs[doc.inputField.name] || "";
          if (!inputVal.trim()) {
            newErrors[doc.inputField.name] = `${doc.label} Number is required`;
          }
        }
      });
    }

    // GST certificate — required for each GST entry when GST is registered
    if (formData.isGSTRegistered === "yes") {
      const companyPAN =
        formData.CompanypanNumber || entityDocInputs["doc_pan_number"] || "";

      gstEntries.forEach((entry, i) => {
        // Check if GST certificate is uploaded
        const hasUploaded =
          uploadedDocs[`gst_file_${i}`]?.filePath ||
          existingPaths[`gst_file_${i}`] ||
          existingDocuments.some(
            (d) =>
              d.key === `gst_file_${i}` && d.files?.some((f) => f.isUploaded),
          );
        if (!hasUploaded) {
          newErrors[`gst_certificate_${i}`] =
            `GST Certificate for entry ${i + 1} (${entry.gstNumber || "GST #" + (i + 1)}) is required`;
        }

        // Validate GST-PAN match — skip in deficiency mode
        if (!isDeficiencyMode && entry.gstNumber && companyPAN) {
          const panMatchResult = validateGSTPANMatch(
            entry.gstNumber,
            companyPAN,
          );
          if (!panMatchResult.isValid) {
            newErrors[`gst_pan_match_${i}`] = panMatchResult.message;
            // Also set the GST error for that specific entry
            setGstErrors((prev) =>
              prev.map((e, ei) => (ei === i ? panMatchResult.message : e)),
            );
          }
        }
      });
    }

    // PAN entity type check — block submit if PAN doesn't match entity type
    // Skip in deficiency mode — PAN was already validated on initial submission
    if (!isDeficiencyMode) {
      const panForCheck =
        formData.CompanypanNumber || entityDocInputs["doc_pan_number"] || "";
      if (panForCheck && formData.entityType) {
        const entityCheck = validatePANEntityType(
          panForCheck,
          formData.entityType,
        );
        if (!entityCheck.isValid) {
          const panField = formData.CompanypanNumber
            ? "CompanypanNumber"
            : "doc_pan_number";
          newErrors[panField] = entityCheck.message;
        }
      }

      // Trust PAN entity type check — PAN of Trust must also match entity type
      const trustPanForCheck = entityDocInputs["doc_panTrust_number"] || "";
      if (trustPanForCheck && formData.entityType) {
        const entityCheck = validatePANEntityType(
          trustPanForCheck,
          formData.entityType,
        );
        if (!entityCheck.isValid) {
          newErrors["doc_panTrust_number"] = entityCheck.message;
        }
      }
    }

    // console.log("[Validate] errors found:", newErrors);
    return newErrors;
  };

  const handleSubmit = async (e, isSaveAsDraft = false) => {
    if (e && e.preventDefault) e.preventDefault();

    // Skip validation for Save Draft
    if (!isSaveAsDraft) {
      const validationErrors = validate();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        console.error("[Submit] Validation errors:", validationErrors);

        // Build a human-readable summary for the toast
        const errorMessages = Object.values(validationErrors);
        const firstError = errorMessages[0];
        const extraCount = errorMessages.length - 1;

        toast.error(
          extraCount > 0
            ? `${firstError} (+${extraCount} more error${extraCount > 1 ? "s" : ""})`
            : firstError,
          { autoClose: 6000 },
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const loggedInUser = getUserData();
      const now = new Date().toISOString();
      const entityPrimaryId = parseInt(
        searchParams.get("onboardingId") ||
          searchParams.get("entityPrimaryId") ||
          "0",
        10,
      );

      // Helper: get filePath — new upload takes priority, falls back to existing path from API
      const getPath = (documentType) =>
        uploadedDocs[documentType]?.filePath ||
        existingPaths[documentType] ||
        "";

      const hasPath = (documentType) => !!getPath(documentType);

      // ── Debug: log all document paths before building payload ──────────────
      const ALL_DOC_KEYS = [
        "AadharCardPath",
        "PANCardPath",
        "PartnershipDeedPath",
        "AadharOfBothPartnersPath",
        "AadharOfBothPartnersPathSecond",
        "UploadLLPCertificatePath",
        "CertificateOfIncorporationPath",
        "DirectorPANPath",
        "DirectorAadharPath",
        "MSMECertificatePath",
        "UtilityBillDocumentPath",
        "PassbookOrCancelledChequePath",
        "UploadSignedFormPath",
        "TrustDeedPath",
        "PANOfTrustPath",
        "AadharOfTrusteePath",
        "PANOfTrusteePath",
      ];
      // console.log("[Submit] Document paths resolved:");
      ALL_DOC_KEYS.forEach((key) => {
        const newPath = uploadedDocs[key]?.filePath;
        const existingPath = existingPaths[key];
        const resolved = getPath(key);
        //  console.log(
        //     `  ${key}: ${resolved ? `✅ "${resolved}"` : "❌ empty"}` +
        //       (newPath
        //         ? ` [new upload]`
        //         : existingPath
        //           ? ` [existing]`
        //           : ` [missing]`),
        // );
      });
      // GST certs
      gstEntries.forEach((_, i) => {
        const key = `gst_file_${i}`;
        const resolved =
          uploadedDocs[key]?.filePath || existingPaths[key] || "";
        // console.log(
        //   `  gst_file_${i}: ${resolved ? `✅ "${resolved}"` : "❌ empty"}`,
        // );
      });

      // Map from doc key (e.g. "doc_aadhar") to the storage key used in uploadedDocs
      // (e.g. "AadharCardPath"). If a new file was uploaded for a deficient doc,
      // its *Def flag should be cleared (set to false) on submit.
      const DOC_KEY_TO_STORAGE_KEY = {
        doc_aadhar: "AadharCardPath",
        doc_pan: "PANCardPath",
        doc_partnershipDeed: "PartnershipDeedPath",
        doc_aadharBoth: "AadharOfBothPartnersPath",
        doc_llpCertificate: "UploadLLPCertificatePath",
        doc_coi: "CertificateOfIncorporationPath",
        doc_directorPan: "DirectorPANPath",
        doc_directorAadhar: "DirectorAadharPath",
        doc_trustDeed: "TrustDeedPath",
        doc_panTrust: "PANOfTrustPath",
        doc_panTrustee: "PANOfTrusteePath",
        doc_aadharTrustee: "AadharOfTrusteePath",
        doc_msme_certificate: "MSMECertificatePath",
        doc_utility_bill: "UtilityBillDocumentPath",
        doc_bank_proof: "PassbookOrCancelledChequePath",
        third_party_file: "UploadSignedFormPath",
      };

      /**
       * Returns the *Def flag value for a doc key.
       * Always returns false on submit — deficiency is cleared when the user submits.
       */
      const getDefFlag = (_docKey) => false;

      /**
       * Returns the isXxx flag value for a doc.
       * - API returned a value (true/false) → pass that exact value
       * - No API value (new entity, first submit) → false
       */
      const getIsFlag = (flagName) => {
        const apiVal = existingIsFlags[flagName];
        return apiVal !== null && apiVal !== undefined ? apiVal : false;
      };

      // Build uploadedList — new uploads first, then existing paths not re-uploaded
      const newUploads = Object.values(uploadedDocs).map((doc) => ({
        success: true,
        message: "",
        id: doc.id ?? 0,
        fileName: doc.fileName || "",
        filePath: doc.filePath || "",
        documentType: doc.documentType || "",
        extension: doc.extension || "",
      }));

      // Add existing paths that were NOT re-uploaded in this session
      const newDocTypes = new Set(
        Object.values(uploadedDocs).map((d) => d.documentType),
      );
      const existingUploads = Object.entries(existingPaths)
        .filter(([key, path]) => path && !newDocTypes.has(key))
        .map(([key, path]) => ({
          success: true,
          message: "",
          id: 0,
          fileName: path.split(/[\\/]/).pop() || key,
          filePath: path,
          documentType: key,
          extension: path.includes(".") ? "." + path.split(".").pop() : "",
        }));

      const uploadedList = [...newUploads, ...existingUploads];

      // Safety net: ensure utility bill path is in uploadedList when GST is "no"
      // (covers edge case where upload API response hasn't been stored yet)
      if (formData.isGSTRegistered === "no") {
        const utilityPath = getPath("UtilityBillDocumentPath");
        const alreadyIncluded = uploadedList.some(
          (u) => u.filePath === utilityPath,
        );
        if (utilityPath && !alreadyIncluded) {
          uploadedList.push({
            success: true,
            message: "",
            id: 0,
            fileName: utilityPath.split(/[\\/]/).pop() || "UtilityBill",
            filePath: utilityPath,
            documentType: "UtilityBillDocumentPath",
            extension: utilityPath.includes(".")
              ? "." + utilityPath.split(".").pop()
              : "",
          });
        }
      }

      // Build gstList — each entry gets its own cert path from indexed slot
      const gstList = gstEntries.map((entry, i) => ({
        id: entry.gstId || i + 1, // use real gstId from API if available
        gstNumber: entry.gstNumber || "",
        state: entry.gstState || "",
        gstAddress: entry.gstAddress || "",
        gstCertificatePath:
          uploadedDocs[`gst_file_${i}`]?.filePath ||
          existingPaths[`gst_file_${i}`] ||
          "",
      }));

      const payload = {
        // ── Identifiers ───────────────────────────────────────────────────────
        entityPrimaryId,
        kycId: existingKycId || 0,
        entityName: formData.parentName || "",
        tradeName: formData.tradeName || "",
        pinCode: formData.pinCode || "",
        city: formData.city || "",
        state: formData.State || "",
        address: formData.address || "",
        natureOfBusiness: formData.natureOfBusiness || "",
        invoiceRequired:
          formData.isInvoiceRequired === "yes"
            ? true
            : formData.isInvoiceRequired === "no"
              ? false
              : null,
        entityType: formData.entityType || "",

        // ── Contact person ────────────────────────────────────────────────────
        contactPersonDetails: {
          contactPersonName: formData.contactPersonName || "",
          designation: formData.contactPersonDesignation || "",
          mobileNumber: formData.contactPersonMobile || "",
          contactPersonEmail: formData.contactEmail || "",
        },

        // ── PAN ───────────────────────────────────────────────────────────────
        panDetails: {
          panCardNumber: entityDocInputs["doc_pan_number"] || "",
          panCardPath: getPath("PANCardPath") || "",
          panCardUploaded: hasPath("PANCardPath"),
          isPANCard: getIsFlag("isPANCard"),
          isPANCardDef: getDefFlag("doc_pan"),
        },

        // ── Aadhar ────────────────────────────────────────────────────────────
        adharDetails: {
          aadharCardNumber: entityDocInputs["doc_aadhar_number"] || "",
          aadharCardPath: getPath("AadharCardPath") || "",
          aadharCardUploaded: hasPath("AadharCardPath"),
          isAadharCard: getIsFlag("isAadharCard"),
          isAadharCardDef: getDefFlag("doc_aadhar"),
        },

        // ── Proprietorship ────────────────────────────────────────────────────
        proprietorshipDetails: {
          aadharCardNumber: entityDocInputs["doc_aadhar_number"] || "",
          aadharCardPath: getPath("AadharCardPath") || "",
          aadharCardUploaded: hasPath("AadharCardPath"),
          isAadharCard: getIsFlag("isAadharCard"),
          isAadharCardDef: getDefFlag("doc_aadhar"),
          panCardNumber: entityDocInputs["doc_pan_number"] || "",
          panCardPath: getPath("PANCardPath") || "",
          panCardUploaded: hasPath("PANCardPath"),
          isPANCard: getIsFlag("isPANCard"),
          isPANCardDef: getDefFlag("doc_pan"),
        },

        // ── Partnership ───────────────────────────────────────────────────────
        partnershipDetails: {
          partnershipDeedPath: getPath("PartnershipDeedPath") || "",
          partnershipDeedUploaded: hasPath("PartnershipDeedPath"),
          isPartnershipDeed: getIsFlag("isPartnershipDeed"),
          isPartnershipDeedDef: getDefFlag("doc_partnershipDeed"),
          aadharNumberOfBothPartners:
            entityDocInputs["doc_aadharBoth_numbers"] || "",
          aadharOfBothPartnersPath: getPath("AadharOfBothPartnersPath") || "",
          aadharOfBothPartnersUploaded: hasPath("AadharOfBothPartnersPath"),
          isAadharOfBothPartners: getIsFlag("isAadharOfBothPartners"),
          isAadharOfBothPartnersDef: getDefFlag("doc_aadharBoth"),
          aadharNumberOfBothPartnersSecond:
            entityDocInputs["doc_aadharBothSecondary_numbers"] || "",
          aadharOfBothPartnersPathSecond:
            getPath("AadharOfBothPartnersPathSecond") || "",
          aadharOfBothPartnersUploadedSecond: hasPath(
            "AadharOfBothPartnersPathSecond",
          ),
          isAadharOfBothPartnersSecond: false,
          isAadharOfBothPartnersDefSecond: getDefFlag(
            "doc_aadharBothSecondary",
          ),
        },

        // ── LLP ─────────────────────────────────────────────────────────────── ───────────────────────────────────────────────────────────────
        llpDetails: {
          llpCrtificateNumber:
            entityDocInputs["doc_llpCertificate_number"] || "",
          uploadLLPCertificatePath: getPath("UploadLLPCertificatePath") || "",
          llpCertificateUploaded: hasPath("UploadLLPCertificatePath"),
          isLLPCertificate: getIsFlag("isLLPCertificate"),
          isLLPCertificateDef: getDefFlag("doc_llpCertificate"),
          partnershipDeedPath: getPath("PartnershipDeedPath") || "",
          partnershipDeedUploaded: hasPath("PartnershipDeedPath"),
          isPartnershipDeed: getIsFlag("isPartnershipDeed"),
          isPartnershipDeedDef: getDefFlag("doc_partnershipDeed"),
          aadharNumberOfBothPartners:
            entityDocInputs["doc_aadharBoth_numbers"] || "",
          aadharOfBothPartnersPath: getPath("AadharOfBothPartnersPath") || "",
          aadharOfBothPartnersUploaded: hasPath("AadharOfBothPartnersPath"),
          isAadharOfBothPartners: getIsFlag("isAadharOfBothPartners"),
          isAadharOfBothPartnersDef: getDefFlag("doc_aadharBoth"),
          aadharNumberOfBothPartnersSecond:
            entityDocInputs["doc_aadharBothSecondary_numbers"] || "",
          aadharOfBothPartnersPathSecond:
            getPath("AadharOfBothPartnersPathSecond") || "",
          aadharOfBothPartnersUploadedSecond: hasPath(
            "AadharOfBothPartnersPathSecond",
          ),
          isAadharOfBothPartnersSecond: false,
          isAadharOfBothPartnersDefSecond: getDefFlag(
            "doc_aadharBothSecondary",
          ),
        },

        // ── Private / Public Limited ──────────────────────────────────────────
        privatePublicLimitedCompanyDetails: {
          certificateOfIncorporationNumber:
            entityDocInputs["doc_coi_number"] || "",
          certificateOfIncorporationPath:
            getPath("CertificateOfIncorporationPath") || "",
          certificateOfIncorporationUploaded: hasPath(
            "CertificateOfIncorporationPath",
          ),
          isCertificateOfIncorporation: getIsFlag(
            "isCertificateOfIncorporation",
          ),
          isCertificateOfIncorporationDef: getDefFlag("doc_coi"),
          directorPANNumber: entityDocInputs["doc_directorPan_number"] || "",
          directorPANPath: getPath("DirectorPANPath") || "",
          directorPanUploaded: hasPath("DirectorPANPath"),
          isDirectorPan: getIsFlag("isDirectorPan"),
          isDirectorPanDef: getDefFlag("doc_directorPan"),
          directorAadharNumber:
            entityDocInputs["doc_directorAadhar_number"] || "",
          directorAadharPath: getPath("DirectorAadharPath") || "",
          directorAadharUploaded: hasPath("DirectorAadharPath"),
          isDirectorAadhar: getIsFlag("isDirectorAadhar"),
          isDirectorAadharDef: getDefFlag("doc_directorAadhar"),
        },

        // ── Trust ─────────────────────────────────────────────────────────────
        trustDetails: {
          trustDeedNumber: entityDocInputs["doc_trustDeed_ref"] || "",
          trustDeedPath: getPath("TrustDeedPath") || "",
          trustDeedUploaded: hasPath("TrustDeedPath"),
          isTrustDeed: getIsFlag("isTrustDeed"),
          isTrustDeedDef: getDefFlag("doc_trustDeed"),
          trustPanNumber: entityDocInputs["doc_panTrust_number"] || "",
          trustPanPath: getPath("PANOfTrustPath") || "",
          trustPanUploaded: hasPath("PANOfTrustPath"),
          isPANOfTrust: getIsFlag("isPANOfTrust"),
          isPANOfTrustDef: getDefFlag("doc_panTrust"),
          trusteePanNumber: entityDocInputs["doc_panTrustee_number"] || "",
          trusteePanPath: getPath("PANOfTrusteePath") || "",
          trusteePanUploaded: hasPath("PANOfTrusteePath"),
          isPANOfTrustee: getIsFlag("isPANOfTrustee"),
          isPANOfTrusteeDef: getDefFlag("doc_panTrustee"),
          trusteeAadharNumber:
            entityDocInputs["doc_aadharTrustee_number"] || "",
          trusteeAadharPath: getPath("AadharOfTrusteePath") || "",
          trusteeAadharUploaded: hasPath("AadharOfTrusteePath"),
          isAadharOfTrustee: getIsFlag("isAadharOfTrustee"),
          isAadharOfTrusteeDef: getDefFlag("doc_aadharTrustee"),
        },

        // ── MSME ──────────────────────────────────────────────────────────────
        msmeDetails: {
          isRegisteredUnderMSME: convertToBoolean(formData.isMSMERegistered),
          msmeRegistrationNumber:
            formData.isMSMERegistered === "yes"
              ? formData.msmeNumber || ""
              : "",
          msmeCertificatePath: getPath("MSMECertificatePath") || "",
          msmeCertificateUploaded: hasPath("MSMECertificatePath"),
          isMSMECertificate: getIsFlag("isMSMECertificate"),
          isMSMECertificateDef: getDefFlag("doc_msme_certificate"),
        },

        // ── Utility bill ──────────────────────────────────────────────────────
        utilityBillDetails: {
          utilityType: formData.utilityType || "",
          providerName: formData.utilityProviderName || "",
          accountOrConsumerNumber: formData.utilityAccountNumber || "",
          addressOnUtilityBill: formData.utilityAddress || "",
          utilityBillDocumentPath: getPath("UtilityBillDocumentPath") || "",
          utilityBillDocumentUploaded: hasPath("UtilityBillDocumentPath"),
          IsUtilityBillDocument: getIsFlag("isUtilityBillDocument"),
          isUtilityBillDocumentDef: getDefFlag("doc_utility_bill"),
        },

        // ── Bank details ──────────────────────────────────────────────────────
        bankDetails: {
          provideBankDetails: formData.hasBankDetails === "yes",
          bankName: formData.bankName || "",
          accountNumber: formData.accountNumber || "",
          ifscCode: formData.ifscCode || "",
          accountType: formData.accountType || "",
          branchName: formData.branchName || "",
          beneficiaryName: formData.beneficiaryName || "",
          relationWithBeneficiary: formData.thirdPartyRelation || "",
          passbookOrCancelledChequePath:
            getPath("PassbookOrCancelledChequePath") || "",
          passbookOrCancelledChequeUploaded: hasPath(
            "PassbookOrCancelledChequePath",
          ),
          isPassbookOrCancelledCheque: getIsFlag("isPassbookOrCancelledCheque"),
          isPassbookOrCancelledChequeDef: getDefFlag("doc_bank_proof"),
          uploadSignedFormPath: getPath("UploadSignedFormPath") || "",
          signedFormUploaded: hasPath("UploadSignedFormPath"),
          isSignedForm: getIsFlag("isSignedForm"),
          isSignedFormDef: getDefFlag("third_party_file"),
        },

        // ── GST ───────────────────────────────────────────────────────────────
        isGSTRegistered: convertToBoolean(formData.isGSTRegistered),
        gstList:
          formData.isGSTRegistered === "yes"
            ? gstList.map((g, i) => {
                // isGST flag: only true if API explicitly returned true, otherwise false
                const apiIsGstValue = existingIsFlags[`isGST_${i}`];
                const isGSTValue = apiIsGstValue === true ? true : false;

                return {
                  id: g.id || i + 1,
                  gstNumber: g.gstNumber || "",
                  state: g.state || "",
                  gstAddress: g.gstAddress || "",
                  gstCertificatePath: g.gstCertificatePath || "",
                  isGST: isGSTValue,
                  isGSTDef: getDefFlag(`gst_file_${i}`),
                };
              })
            : [],

        // ── Uploaded file list ────────────────────────────────────────────────
        uploadedList,

        // ── Metadata ─────────────────────────────────────────────────────────
        createdBy: modifiedbyFromURL || formData.parentName,
        createdOn: now,
        modifiedBy: modifiedbyFromURL || formData.parentName,
        modifiedOn: now,
        isDocumentUploaded: isSaveAsDraft ? false : true,
        isSaveAsDraft,
      };

      // console.log("[Submit] Payload:", payload);
      const result = await sendEntityKYCFormDataAction(payload);
      if (result?.success) {
        if (isSaveAsDraft) {
          toast.success(result.message || "Draft saved successfully!");
        } else {
          toast.success(result.message || "KYC submitted successfully!");
          const entityName =
            formData.parentName || searchParams.get("EntityName") || "";
          const entityId =
            searchParams.get("entityId") || searchParams.get("EntityId") || "";
          navigate(
            `/thank-you?entityName=${encodeURIComponent(entityName)}&entityId=${encodeURIComponent(entityId)}`,
          );
        }
      } else {
        toast.error(
          result?.message ||
            result?.error ||
            (isSaveAsDraft ? "Failed to save draft" : "Failed to submit KYC"),
          { autoClose: 6000 },
        );
      }
    } catch (err) {
      console.error("[Submit] error:", err);
      toast.error(err?.message || "An error occurred while submitting");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Document upload handler ────────────────────────────────────────────────
  const handleDocumentUpload = async (docKey, file) => {
    if (!file) return;

    // gst_file_0, gst_file_1 etc. → API type is always GSTCertificatePath
    // but we store the response keyed by the indexed slot so each entry is separate
    const isIndexedGst = /^gst_file_\d+$/.test(docKey);
    const documentType = isIndexedGst
      ? "GSTCertificatePath"
      : DOC_KEY_TO_API_TYPE[docKey] || docKey;
    // Storage key: use the full indexed key so each GST cert is stored separately
    const storageKey = isIndexedGst ? docKey : documentType;

    const entityId =
      searchParams.get("entityId") || searchParams.get("EntityId") || "";

    // console.log(
    //   `[Upload] Starting: docKey="${docKey}" → documentType="${documentType}" → storageKey="${storageKey}" | file="${file.name}"`,
    // );

    const result = await uploadOnboardingDocumentAction({
      entityId,
      id: 0,
      documentType,
      fileName: file.name.replace(/\.[^/.]+$/, ""),
      file,
    });

    if (result.success && result.data) {
      // console.log(
      //   `[Upload] ✅ SUCCESS: storageKey="${storageKey}" | filePath="${result.data.filePath}"`,
      // );
      setUploadedDocs((prev) => {
        const updated = {
          ...prev,
          [storageKey]: { ...result.data, documentType },
        };
        // console.log(
        //     "[Upload] uploadedDocs state:",
        //     Object.fromEntries(
        //       Object.entries(updated).map(([k, v]) => [k, v.filePath]),
        //     ),
        // );
        return updated;
      });
    } else if (!result.success) {
      console.error(
        `[Upload] ❌ FAILED: docKey="${docKey}" | error="${result.error}"`,
      );
      toast.error(`Failed to upload ${documentType}: ${result.error}`);
    }

    return result;
  };

  // ── Aadhar Visibility with OTP ────────────────────────────────────────────────
  const {
    otpModalOpen,
    currentAadharField,
    adminEmail,
    entityPrimaryId: aadharEntityId,
    handleEyeIconClick,
    handleOtpVerifySuccess,
    handleCloseOtpModal,
    getAadharDisplayValue,
    isAadharVisible,
    getCurrentFieldLabel,
    isAadharField,
  } = useAadharVisibilityData(
    entityDocInputs,
    parseInt(onboardingIdFromUrl, 10) || 0,
  );

  return {
    formData,
    errors,
    setErrors,
    isSubmitting,
    // departmentOptions, // TODO: re-enable when department dropdown is added
    handleChange,
    handleBlur,
    handleSubmit,
    handleSaveDraft: (e) => handleSubmit(e, true),
    handleDocumentUpload,
    existingDocuments,
    gstEntries,
    addGstEntry,
    removeGstEntry,
    handleGstChange,
    handleGstBlur,
    gstFiles,
    handleGstFileChange,
    gstErrors,
    utilityFile,
    setUtilityFile,
    msmeFile,
    setMsmeFile,
    entityId,
    thirdPartyFile,
    setThirdPartyFile,
    entityFiles,
    handleEntityFileChange,
    entityDocInputs,
    handleEntityDocInputChange,
    handleEntityDocInputBlur,
    bankFiles,
    handleBankFileChange,
    showThirdParty,
    beneficiaryMatchStatus,
    entityCategory,
    // entityLOBType,   // TODO: re-enable when shown in UI
    contactPersonEmail,
    spocEmailAddress,
    deficiencyMap,
    isDeficiencyMode,
    // Aadhar visibility
    otpModalOpen,
    currentAadharField,
    adminEmail,
    aadharEntityId,
    handleEyeIconClick,
    handleOtpVerifySuccess,
    handleCloseOtpModal,
    getAadharDisplayValue,
    isAadharVisible,
    getCurrentFieldLabel,
    isAadharField,
  };
};
